import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import { IconButton } from "@mui/material";
import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiResponse, IEntity } from "common/models/ApiResponse";
import { PermissionType } from "common/models/access/permissionType";
import { useDialogLoader } from "contexts/DialogLoader";
import { useDialog } from "contexts/ToolsProvider/DialogContext";
import { useNavigate } from "react-router-dom";
import { showError, showSuccess } from "utils/showMessages";
import { getFirstServerError } from "utils/formUtils";

export default function ActionColumn<
  T extends string | number,
  TEntity extends IEntity
>(props: {
  id: T;
  dataKey: string;
  modalFetcher: (id: any) => Promise<ApiResponse<TEntity>>;
  deleteMutation: UseMutationOptions<
    ApiResponse<number>,
    Error,
    any,
    unknown
  >;
  permissions?: PermissionType;
}) {
  const { id, dataKey, deleteMutation, permissions } = props;

  const queryClient = useQueryClient();
  const navigateTo = useNavigate();
  const showDialog = useDialog();

  const { showDialog: showModalDialog } = useDialogLoader<TEntity>();

  const handleShowDialog = async (id: string | number) => {
    await showModalDialog({ id: id });
  };

  const { mutate } = useMutation({
    ...deleteMutation,
  });

  const handleDeleteDialog = async (id: string | number) => {
    const confirmed = await showDialog({
      title: "Are you sure you want to delete?",
      // message: `${id} clicked`,
    });

    if (confirmed) {
      await mutate(id, {
        onSettled(response) {
          if ((response?.apiData || 0) >= 1) {
            queryClient.invalidateQueries({
              queryKey: [dataKey],
            });
            showSuccess("Record deleted successfully!");
          } else {
            const errorMsg =
              getFirstServerError(response?.errors) ||
              (response?.title &&
              response.title !== "One or more validation errors occurred."
                ? response.title
                : null) ||
              "Failed to delete record";
            showError(errorMsg);
          }
        },
      });
    }
  };

  return (
    <>
      {permissions?.View && (
        <IconButton
          title="View"
          color="secondary"
          onClick={async () => await handleShowDialog(id)}
        >
          <EyeOutlined />
        </IconButton>
      )}
      {permissions?.Edit && (
        <IconButton
          title="Edit"
          color="primary"
          onClick={() => {
            navigateTo(`edit/${id}`);
          }}
        >
          <EditOutlined />
        </IconButton>
      )}
      {permissions?.Delete && (
        <IconButton
          title="Delete"
          color="error"
          onClick={async () => await handleDeleteDialog(id)}
        >
          <DeleteOutlined />
        </IconButton>
      )}
    </>
  );
}
